import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronsLeft, ChevronsRight, type LucideIcon } from 'lucide-react';
import { ZafroiLogo } from '../branding/ZafroiLogo';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import { cn } from '../../lib/utils';

export interface SidebarNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Destination href. External URLs are detected via "http(s)://". */
  href: string;
  /** Inline badge (e.g. unread count, "Beta"). */
  badge?: React.ReactNode;
  /** Force the item to render disabled. */
  disabled?: boolean;
  /** When true, an exact-match is required to highlight this item as active. */
  end?: boolean;
}

export interface SidebarNavGroup {
  id: string;
  /** Group label. Hidden when sidebar is collapsed. */
  label?: string;
  items: SidebarNavItem[];
}

export interface SharedSidebarShellProps {
  nav: SidebarNavGroup[];
  collapsed?: boolean;
  onToggle?: () => void;
  /** Custom header content. Defaults to <ZafroiLogo />. */
  header?: React.ReactNode;
  /** Footer — version pill, environment badge, support link, etc. */
  footer?: React.ReactNode;
  /** Optional subtitle shown beside the wordmark when expanded (e.g. product name). */
  productName?: string;
  className?: string;
}

/**
 * SharedSidebarShell — the graphite left nav that anchors every ZAFROI app.
 *
 * Receives a nav config and renders the chrome; consumers supply nothing
 * domain-specific. Uses react-router's `NavLink` for active-state highlighting,
 * falling back to anchor semantics for absolute URLs.
 */
export function SharedSidebarShell({
  nav,
  collapsed = false,
  onToggle,
  header,
  footer,
  productName,
  className,
}: SharedSidebarShellProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <aside
        data-collapsed={collapsed ? 'true' : 'false'}
        className={cn(
          'flex h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200',
          collapsed ? 'w-[68px]' : 'w-[248px]',
          className,
        )}
      >
        <div className="flex h-[72px] items-center justify-between gap-2 border-b border-sidebar-border/60 px-3">
          <div className="min-w-0 flex-1">
            {header ?? (
              <ZafroiLogo
                tone="light"
                variant={collapsed ? 'mark' : 'lockup'}
                subtitle={collapsed ? undefined : productName}
                size={collapsed ? 28 : 24}
              />
            )}
          </div>
          {onToggle && (
            <button
              type="button"
              onClick={onToggle}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sidebar-muted transition-colors hover:bg-white/[0.06] hover:text-sidebar-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-accent/40"
            >
              {collapsed ? (
                <ChevronsRight className="h-4 w-4" />
              ) : (
                <ChevronsLeft className="h-4 w-4" />
              )}
            </button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-2">
          {nav.map((group, groupIndex) => (
            <div
              key={group.id}
              className={cn(groupIndex > 0 && 'mt-3 border-t border-sidebar-border pt-3')}
            >
              {group.label && !collapsed && (
                <div className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-sidebar-muted">
                  {group.label}
                </div>
              )}
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.id}>
                    {collapsed ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SidebarLink item={item} collapsed />
                        </TooltipTrigger>
                        <TooltipContent side="right" sideOffset={10}>
                          {item.label}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <SidebarLink item={item} />
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {footer && (
          <div
            className={cn(
              'border-t border-sidebar-border px-3 py-2 text-xs text-sidebar-muted',
              collapsed && 'flex justify-center px-2',
            )}
          >
            {footer}
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}

interface SidebarLinkProps {
  item: SidebarNavItem;
  collapsed?: boolean;
}

const SidebarLink = React.forwardRef<HTMLAnchorElement, SidebarLinkProps>(
  ({ item, collapsed }, ref) => {
    const Icon = item.icon;
    const isExternal = /^https?:\/\//i.test(item.href);

    const classes = ({ isActive }: { isActive: boolean }) =>
      cn(
        'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors',
        'text-sidebar-foreground/80 hover:bg-white/[0.06] hover:text-sidebar-foreground',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-accent/60',
        isActive && 'bg-white/[0.08] text-sidebar-foreground',
        item.disabled && 'pointer-events-none opacity-50',
        collapsed && 'justify-center px-0',
      );

    const content = ({ isActive }: { isActive: boolean }) => (
      <>
        <span
          className={cn(
            'inline-flex h-5 w-5 items-center justify-center text-sidebar-muted transition-colors group-hover:text-sidebar-foreground',
            isActive && 'text-sidebar-accent',
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            {item.badge && (
              <span className="ml-auto text-[10px] font-medium uppercase text-sidebar-muted">
                {item.badge}
              </span>
            )}
          </>
        )}
      </>
    );

    if (isExternal) {
      return (
        <a
          ref={ref}
          href={item.href}
          target="_blank"
          rel="noreferrer"
          className={classes({ isActive: false })}
        >
          {content({ isActive: false })}
        </a>
      );
    }

    return (
      <NavLink to={item.href} end={item.end} className={classes} ref={ref}>
        {({ isActive }) => content({ isActive })}
      </NavLink>
    );
  },
);
SidebarLink.displayName = 'SidebarLink';
